import AcxMainTextField, {
    AcxMainTextFieldRef,
} from "components/UI/AcxMainTextField";
import { useCurrentStep } from "./Step";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { AcxMenuItem, AcxMenuV2 } from "components/UI/Menu/AcxMenu";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import { ChainSourceStore } from "../../Stores/ChainSourceStore";
import { observer } from "mobx-react";
import { Typography } from "@mui/material";
import { getCaretCoordinates } from "./utils";
import _ from "lodash";
import {
    groupVariablesByProducingLLMModel,
    LLMVariableGroup,
    SlimLLMVariable,
} from "models/LLMs/LLMVariable";
import { useAddPromptParentsConfirmation } from "../ChainSource/useAddPromptParentsConfirmation";

type ContextMenuState = {
    /**
     * 0 based index into the prompt where the @ character is located
     */
    startIndex: number;
    position: { top: number; left: number };
} | null;

export function StepPrompt() {
    const store = useStore(LLMClassifierBuilderStore);
    const currentStep = useCurrentStep();
    const textareaRef = useRef<AcxMainTextFieldRef>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const [contextMenu, setContextMenu] = useState<ContextMenuState>(null);
    const { detectPromptParents, confirmationDialog } =
        useAddPromptParentsConfirmation();

    function forceFocusTextareaRef() {
        if (!textareaRef.current) return;

        setTimeout(() => textareaRef.current?.focus(), 0);
    }

    const contextActionValue = useMemo(() => {
        if (!contextMenu || !textareaRef.current) return;

        const caret = textareaRef.current.selectionStart;
        if (caret === null) return;

        const actionValue = currentStep.configuration.prompt.substring(
            contextMenu.startIndex + 1,
            caret,
        );
        return actionValue;
    }, [contextMenu, currentStep.configuration.prompt]);

    const cancelContextAction = useCallback(() => {
        setContextMenu(null);
    }, []);

    const reopenContextAction = useCallback(() => {
        if (!contextMenu || !textareaRef.current) return;
        textareaRef.current.setSelectionRange(
            contextMenu.startIndex + 1,
            contextMenu.startIndex + 1,
            "forward",
        );
        // Context menu is already set, no need to update state
    }, [contextMenu]);

    function getCaretAbsolutePosition() {
        if (!textareaRef.current) return;
        const coordinates = getCaretCoordinates(
            textareaRef.current,
            textareaRef.current.selectionEnd ?? 0,
        );
        const rect = textareaRef.current.getBoundingClientRect();
        return {
            top:
                coordinates.top +
                coordinates.height +
                window.scrollY +
                rect.top -
                textareaRef.current.scrollTop,
            left: coordinates.left + window.scrollX + rect.left,
        };
    }

    function checkForContextActionTrigger(
        oldText: string,
        newText: string,
        caret: number,
    ) {
        // Check if user just typed @ by comparing old and new text
        if (newText.length > oldText.length && caret > 0) {
            const insertedChar = newText[caret - 1];
            if (insertedChar === "@") {
                const position = getCaretAbsolutePosition();
                if (!position) return;
                setContextMenu({ startIndex: caret - 1, position });
                forceFocusTextareaRef();
            }
        }
    }

    const handleSubmitPromptContextAction = useCallback(
        (value: string) => {
            if (!textareaRef.current || !contextMenu) return;

            let cursorPosition: number;

            if (!contextActionValue) {
                const prompt = currentStep.configuration.prompt;
                const before = prompt.substring(0, contextMenu.startIndex);
                const after = prompt.substring(contextMenu.startIndex + 1);
                currentStep.configuration.setPrompt(before + value + after);
                cursorPosition = before.length + value.length;
            } else {
                const prompt = currentStep.configuration.prompt;
                const searchTerm = `@${contextActionValue}`;
                const replaceIndex = prompt.indexOf(searchTerm);
                currentStep.configuration.setPrompt(
                    prompt.replace(searchTerm, value),
                );
                cursorPosition = replaceIndex + value.length;
            }
            cancelContextAction();
            // Restore focus to textarea after selection and position cursor.
            // We have to use a sort of hacky setTimeout here because calling focus() on an element
            // does not guarantee the element is actually focused after the invocation due to how react works.
            // The timeout basically allows us to have the code invoked after reacts current render, so we can
            // actually ensure the element is focused after closing the menu AND so that we can set the cursor position,
            // otherwise the cursor would be at the end of the textarea.
            setTimeout(() => {
                textareaRef.current?.focus();
                setTimeout(() => {
                    textareaRef.current?.setSelectionRange(
                        cursorPosition,
                        cursorPosition,
                    );
                }, 0);
            }, 0);
        },
        [
            contextMenu,
            contextActionValue,
            cancelContextAction,
            currentStep.configuration,
        ],
    );

    useEffect(() => {
        function handleEscape(event: KeyboardEvent) {
            if (event.key === "Escape") {
                cancelContextAction();
            }
        }
        window.addEventListener("keydown", handleEscape);
        return () => {
            window.removeEventListener("keydown", handleEscape);
        };
    }, [cancelContextAction]);

    function autoUpdatePropertyNames(
        oldAngleBracketVariables: string[],
        newAngleBracketVariables: string[],
    ) {
        if (_.isEqual(oldAngleBracketVariables, newAngleBracketVariables))
            return;

        const oldCounts = countOccurrences(oldAngleBracketVariables);
        const newCounts = countOccurrences(newAngleBracketVariables);

        let oldVariable: string | undefined;
        let newVariable: string | undefined;

        for (const key of oldCounts.keys()) {
            const oldCount = oldCounts.get(key) ?? 0;
            const newCount = newCounts.get(key) ?? 0;
            if (newCount < oldCount) {
                oldVariable = key;
                break;
            }
        }

        for (const key of newCounts.keys()) {
            const oldCount = oldCounts.get(key) ?? 0;
            const newCount = newCounts.get(key) ?? 0;
            if (newCount > oldCount) {
                newVariable = key;
                break;
            }
        }

        // Handles going from <some name> to <> and vice versa.
        // We still know which selectors were using the property name even if it's now empty
        // because we store the long property name with the step id prefixed.
        if (oldVariable && !newVariable) {
            newVariable = "";
        } else if (!oldVariable && newVariable) {
            oldVariable = "";
        }

        if (
            newVariable === undefined ||
            oldVariable === undefined ||
            newVariable === oldVariable
        )
            return;

        const builder = store.classifier.implementation.logicChainBuilder;

        for (const step of builder.steps) {
            step.updatePropertyNames(currentStep.id, oldVariable, newVariable);
        }
    }

    return (
        <>
            <AcxMainTextField
                id={`${currentStep.reactKey}-prompt`}
                labelText="Prompt"
                required
                multiline
                multilinePadding
                fullWidth
                showCopyButton
                rowsMin={12}
                rowsMax={20}
                value={currentStep.configuration.prompt}
                ref={textareaRef}
                onChange={(event) => {
                    const newText = event.target.value;
                    const oldText = currentStep.configuration.prompt;

                    const oldVariables =
                        currentStep.configuration.getPotentialOutputVariables();
                    currentStep.configuration.setPrompt(newText);
                    const newVariables =
                        currentStep.configuration.getPotentialOutputVariables();

                    if (oldVariables && newVariables) {
                        autoUpdatePropertyNames(oldVariables, newVariables);
                    }

                    const caret = textareaRef.current?.selectionStart;
                    if (caret === null || caret === undefined) return;

                    // Check if user typed @ to open context menu
                    if (!contextMenu) {
                        checkForContextActionTrigger(oldText, newText, caret);
                        return;
                    }

                    // Handle side effects for existing context action
                    if (
                        newText.length <= contextMenu.startIndex ||
                        caret <= contextMenu.startIndex
                    ) {
                        cancelContextAction();
                    }
                }}
                onKeyDown={(event) => {
                    if (!textareaRef.current) return;

                    // forward key events to menu when it's open
                    const keysToRedirectToMenu = [
                        "ArrowUp",
                        "ArrowDown",
                        "Enter",
                    ];
                    if (contextMenu) {
                        if (
                            menuRef.current &&
                            keysToRedirectToMenu.includes(event.key)
                        ) {
                            event.preventDefault();
                            event.stopPropagation();
                            const keyboardEvent = new KeyboardEvent("keydown", {
                                key: event.key,
                                code: event.code,
                                bubbles: true,
                                cancelable: true,
                            });

                            menuRef.current.focus();
                            menuRef.current.dispatchEvent(keyboardEvent);
                        }
                    }
                }}
                onFocus={() => {
                    reopenContextAction();
                }}
                onBlur={() => {
                    // Skipped while the @ menu is open, since choosing from it
                    // blurs the textarea and its options are already declared
                    // parents. Otherwise, offer to declare what the prompt names.
                    if (!contextMenu) {
                        detectPromptParents(currentStep.configuration.prompt);
                    }
                }}
            />
            <ConversationMetadataMenu
                open={contextMenu !== null}
                onClose={() => {
                    setContextMenu(null);
                    // Restore focus to textarea when menu closes
                    textareaRef.current?.focus();
                }}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu?.position}
                search={contextActionValue}
                MenuListProps={{
                    ref: menuRef as any,
                    tabIndex: -1,
                }}
                onItemClick={(option) => {
                    const variable = `{{${option.name}}}`;
                    handleSubmitPromptContextAction(variable);
                }}
            />
            {confirmationDialog}
        </>
    );
}

type ConversationMetadataMenuProps = {
    search?: string;
    onItemClick?: (option: SlimLLMVariable) => void;
} & React.ComponentProps<typeof AcxMenuV2>;

const ConversationMetadataMenu = observer(function ({
    search,
    onItemClick,
    ...props
}: ConversationMetadataMenuProps) {
    const store = useStore(LLMClassifierBuilderStore);
    const chainSourceStore = useStore(ChainSourceStore);
    const currentStep = useCurrentStep();

    function satisfiesSearch(
        option: SlimLLMVariable,
        group: LLMVariableGroup,
    ): boolean {
        if (!search) return true;

        const lowerSearch = search.toLowerCase();

        const isInGroupName = group.label.toLowerCase().includes(lowerSearch);
        const isInOption = option.label.toLowerCase().includes(lowerSearch);

        return isInGroupName || isInOption;
    }

    const stepVariableGroups = store.stepVariableOptions
        .filter((group) => group.label !== currentStep.name)
        .map((group) => ({
            ...group,
            label: `Step: ${group.label}`,
        }));

    const variableGroups = groupVariablesByProducingLLMModel(
        store.variableGroups,
    ).concat(stepVariableGroups);
    const filteredVariableGroups = variableGroups
        .map((group) => ({
            ...group,
            variables: group.variables.filter((variable) =>
                chainSourceStore.isVariableReferenceable(variable),
            ),
        }))
        // A model group with no parents to offer is dropped entirely rather than
        // left as a permanently empty heading. Groups emptied by the search term
        // still show, so it stays clear the search is what hid them.
        .filter((group) => group.variables.length > 0)
        .map((group) => ({
            ...group,
            options: group.variables.filter((option) =>
                satisfiesSearch(option, group),
            ),
        }));

    return (
        <AcxMenuV2
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            disableAutoFocus
            disableAutoFocusItem
            disableEnforceFocus
            slotProps={{
                paper: {
                    style: {
                        maxHeight: "calc(100dvh/4)",
                        // The menu sizes to its content up to this, and items wrap past it.
                        maxWidth: "min(32rem, 90vw)",
                    },
                },
            }}
            {...props}
        >
            {/* Menu needs a flat array of children to traverse for keyboard
            navigation - wrapping each group in a Fragment hides its items. */}
            {filteredVariableGroups.flatMap((group) => [
                <Typography
                    key={`${group.label}-header`}
                    component="div"
                    sx={(theme) => ({
                        position: "sticky",
                        top: 0,
                        padding: theme.spacing(1),
                        fontSize: "13px",
                        fontWeight: "bold",
                        background: "white",
                        width: "100%",
                        zIndex: 100,
                        userSelect: "none",
                        opacity: group.options.length === 0 ? "50%" : "100%",
                    })}
                    tabIndex={-1}
                    aria-hidden="true"
                >
                    {group.label}
                </Typography>,
                ...group.options.map((option) => (
                    <AcxMenuItem
                        key={`${group.label}-${option.label}`}
                        // MenuItem is nowrap by default, which clips a long
                        // option instead of running onto a second line.
                        sx={{
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                        }}
                        onClick={() => {
                            onItemClick?.(option);
                        }}
                    >
                        {option.label}
                    </AcxMenuItem>
                )),
            ])}
        </AcxMenuV2>
    );
});

function countOccurrences(array: string[]): Map<string, number> {
    if (array.length === 0) return new Map<string, number>();

    const map = new Map<string, number>();
    for (const item of array) {
        map.set(item, (map.get(item) ?? 0) + 1);
    }

    return map;
}
